import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useFavouritesQuery, useMembersQuery } from '@platform/state/api';
import { useLabel } from '@platform/hooks/useLabel';
import { cx } from '@platform/core/lib/cx';
import { routes } from '@platform/core/lib/routes';
import { CollapsibleGroup } from '@platform/engine/menu/CollapsibleGroup';
import { MenuHeader } from '@platform/engine/menu/MenuHeader';
import { useMenuOpenState } from '@platform/engine/menu/useMenuOpenState';
import { useInbox } from '@platform/features/users/inbox/useInbox';
import { useAppSelector } from '@platform/state/hooks';
import { selectUi } from '@platform/state/selectors';
import { useMyWork } from './pages/work/useMyWork';
import { appRoutes } from './shell/appRoutes';
import { HomeMenuLink } from './shell/HomeMenuLink';
import { moduleCount } from './shell/moduleCount';
import { homePinnedKeys } from './shell/homePinnedKeys';
import { usePendingInvitations } from './shell/usePendingInvitations';
import { useShellNav } from './shell/useShellNav';
import { useWorkspaceMeta } from './shell/useWorkspaceMeta';
import type { IHomeMenuProps } from './IHomeMenuProps';
import '@platform/engine/menu/menu.css';
import './shell/homeMenu.css';

const RECENT_LIMIT = 30;

/**
 * Ліве меню Головної (прототип v4 G:L311–345, UIUX left-menu): «Робочий простір / Головна / тип · N учасників», розгортні групи «Моя робота»
 * (призначені мені, термін цього тижня, прострочено — колір попередження, непрочитані у вхідних, нещодавні, обране), «Закріплені»,
 * «Бізнес-аналітика» (огляд, навантаження, тенденції) і «Учасники» (учасники, запрошення). Розгорнутість груп пам'ятається.
 * Лічильник запрошень — ті, що очікують відповіді (як «Запрошення 0» прототипу).
 */
export function HomeMenu({ ws }: IHomeMenuProps) {
  const { t } = useTranslation();
  const label = useLabel();
  const { meta } = useWorkspaceMeta(ws);
  const { me, catalog } = useShellNav(ws);
  const { counts } = useMyWork(ws);
  const { unread } = useInbox();
  const { data: favourites } = useFavouritesQuery();
  const { data: members } = useMembersQuery(ws, { skip: !ws });
  const invitations = usePendingInvitations(ws);
  const openState = useMenuOpenState('home');
  const collapsed = useAppSelector((s) => selectUi(s).leftMenuCollapsed);
  const group = (id: string, title: string, children: ReactNode) => (
    <CollapsibleGroup title={title} open={openState.isOpen(id, true)} onToggle={() => openState.toggle(id, true)}>{children}</CollapsibleGroup>
  );
  const inWs = (list: Array<{ workspaceId: string }> | undefined) => list?.filter((r) => r.workspaceId === ws).length;
  // «Нещодавні» — 30 записів простору, оновлених останніми (сторінка «Нещодавні»): лічильник — за кількостями каталогу
  const records = catalog?.modules.filter((m) => m.installed).reduce((sum, m) => sum + (moduleCount(m) ?? 0), 0);
  return (
    <aside className={cx('po-menu mnav', collapsed && 'is-collapsed')} aria-label={t('homeNav.label')}>
      <nav className="po-menu__nav mnav__nav" aria-label={t('homeNav.label')}>
        <MenuHeader eyebrow={t('shell.workspace')} title={t('homeNav.label')} meta={meta} />
        {group('work', t('homeNav.myWork'), (
          <>
            <HomeMenuLink to={appRoutes.work('mine')} label={t('homeNav.assignedToMe')} count={counts?.mine} />
            <HomeMenuLink to={appRoutes.work('week')} label={t('homeNav.dueThisWeek')} count={counts?.week} />
            <HomeMenuLink to={appRoutes.work('overdue')} label={t('homeNav.overdue')} count={counts?.overdue} warning />
            <HomeMenuLink to={`${routes.inbox()}?show=unread&space=${encodeURIComponent(ws)}`} label={t('homeNav.unreadInbox')} count={unread} />
            <HomeMenuLink to={appRoutes.recent()} label={t('homeNav.recent')} count={records === undefined ? undefined : Math.min(RECENT_LIMIT, records)} />
            <HomeMenuLink to={appRoutes.favourites()} label={t('homeNav.favourites')} count={inWs(favourites) ?? 0} />
          </>
        ))}
        {group('pinned', t('homeNav.pinned'), homePinnedKeys(me, catalog, ws).map((key) => {
          const summary = catalog?.modules.find((m) => m.key === key);
          return <HomeMenuLink key={key} to={routes.module(key)} label={label(summary?.name) || key} count={moduleCount(summary)} />;
        }))}
        {group('bi', t('homeNav.bi'), (
          <>
            <HomeMenuLink to={appRoutes.bi('overview')} label={t('homeNav.biOverview')} />
            <HomeMenuLink to={appRoutes.bi('workload')} label={t('homeNav.biWorkload')} />
            <HomeMenuLink to={appRoutes.bi('trends')} label={t('homeNav.biTrends')} />
          </>
        ))}
        {group('members', t('homeNav.members'), (
          <>
            <HomeMenuLink to={appRoutes.members()} label={t('homeNav.membersItem')} count={members?.length} end />
            <HomeMenuLink to={appRoutes.invitations()} label={t('homeNav.invitations')} count={invitations} />
          </>
        ))}
      </nav>
    </aside>
  );
}
